import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Plus, Search, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/form-field';
import { Badge, Card, Input, Select, Skeleton } from '@/components/ui/primitives';
import { EmptyRow, Pagination, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { CustomerItem, Paginated } from '@/lib/types';
import { CustomerDialog } from './customer-form';

export function CustomersPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [archived, setArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search);
      setPage(1);
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const params = new URLSearchParams({ page: String(page), pageSize: '25' });
  if (q) params.set('q', q);
  if (type) params.set('type', type);
  if (archived) params.set('archived', 'true');

  const { data, isLoading } = useQuery({
    queryKey: ['customers', 'list', params.toString()],
    queryFn: () => api<Paginated<CustomerItem>>(`/customers?${params}`),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title="Kunden"
        description="Privat- und Geschäftskunden mit Ansprechpartnern"
        actions={
          can('customers.create') && (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus /> Neuer Kunde
            </Button>
          )
        }
      />
      <Card>
        <div className="flex flex-col gap-2 border-b p-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-8" placeholder="Name, Kundennummer, E-Mail, Ort, Ansprechpartner" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Suche" />
          </div>
          <Select value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} className="sm:w-44" aria-label="Kundenart">
            <option value="">Alle Kundenarten</option>
            <option value="BUSINESS">Geschäftskunden</option>
            <option value="PRIVATE">Privatkunden</option>
          </Select>
          <Select value={archived ? '1' : ''} onChange={(e) => { setArchived(e.target.value === '1'); setPage(1); }} className="sm:w-36" aria-label="Archiv">
            <option value="">Aktive</option>
            <option value="1">Archivierte</option>
          </Select>
        </div>
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>Kunde</TH>
              <TH className="hidden md:table-cell">Kontakt</TH>
              <TH className="hidden lg:table-cell">Ort</TH>
              <TH className="text-right">Offene Tickets</TH>
            </TR>
          </THead>
          <TBody>
            {isLoading && (
              <TR>
                <TD colSpan={4}>
                  <Skeleton className="h-32" />
                </TD>
              </TR>
            )}
            {data?.items.length === 0 && <EmptyRow colSpan={4}>Keine Kunden gefunden.</EmptyRow>}
            {data?.items.map((c) => (
              <TR key={c.id} className="cursor-pointer" onClick={() => navigate(`/customers/${c.id}`)}>
                <TD>
                  <div className="flex items-center gap-3">
                    {c.type === 'BUSINESS' ? <Building2 className="size-4 text-muted-foreground" /> : <User className="size-4 text-muted-foreground" />}
                    <div className="min-w-0">
                      <p className="truncate font-medium">{c.name}</p>
                      <p className="font-mono text-xs text-muted-foreground">{c.number}</p>
                    </div>
                  </div>
                </TD>
                <TD className="hidden text-muted-foreground md:table-cell">
                  <p className="truncate">{c.email ?? '–'}</p>
                  {c.phone && <p className="text-xs">{c.phone}</p>}
                </TD>
                <TD className="hidden lg:table-cell">{[c.postalCode, c.city].filter(Boolean).join(' ') || '–'}</TD>
                <TD className="text-right">{c.openTickets > 0 ? <Badge>{c.openTickets}</Badge> : <span className="text-muted-foreground">0</span>}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
      </Card>
      <CustomerDialog
        customer={null}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSaved={(c) => {
          qc.invalidateQueries({ queryKey: ['customers'] });
          navigate(`/customers/${c.id}`);
        }}
      />
    </>
  );
}
